import {
  CmsComponentGrid,
  ContentComponentLibraryProvider,
} from '@johnlewispartnership/wtr-content-component-library';
import type { CmsComponentData } from '@johnlewispartnership/wtr-content-component-library';
import type { Config } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/config/types';
import { FragmentFeatureFlagsProvider } from '@johnlewispartnership/wtr-website-decisioner-client/express';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import type { PreloadedData } from 'server/types';
import { logger } from 'logger';
import { BESPOKE_CONTENT_FRAGMENT_PATHS } from 'server/paths';

import AnalyticsSetup from './components/AnalyticsSetup';
import cmsComponentMap from './componentMap';
// For now, we always import the global grid styles
import './styles/grid.scss';
import siteWideBannerStyles from './styles/sitewide-banner.module.scss';

type FragmentAppContentProps = {
  componentData: CmsComponentData[];
  /**
   * componentLibraryConfig: for environment based config options that need to come from server
   */
  componentLibraryConfig: Omit<Config, 'componentMap'>;
  contentPath: string;
};

type FragmentAppProps = FragmentAppContentProps & {
  preloadedData: PreloadedData;
};

const FragmentAppContent = ({
  componentData,
  componentLibraryConfig,
  contentPath,
}: FragmentAppContentProps) => {
  const { trackEvent } = useAnalytics();

  const isSitewideBanner = contentPath === BESPOKE_CONTENT_FRAGMENT_PATHS['/sitewide-banner'].path;
  const position = isSitewideBanner ? 'sitewide-banner' : 'root';

  /**
   * For hardcoded isomorphic config options
   */
  const config: Config = {
    componentMap: cmsComponentMap,
    imageQuality: 'highQuality85',
    onAnalyticsEvent: trackEvent,
    headFunction: meta => meta,
    onError: logger.error,
    ...componentLibraryConfig,
  };

  const contentComponents = (
    <ContentComponentLibraryProvider config={config}>
      <CmsComponentGrid childComponents={componentData} position={position} preloadImages />
    </ContentComponentLibraryProvider>
  );

  if (isSitewideBanner) {
    return (
      <div
        role="region"
        aria-label="promotional banner"
        className={siteWideBannerStyles.container}
        data-testid="site-banner"
        suppressHydrationWarning
      >
        {contentComponents}
      </div>
    );
  }

  return contentComponents;
};

const FragmentApp = ({
  componentData,
  componentLibraryConfig,
  contentPath,
  preloadedData,
}: FragmentAppProps) => (
  <FragmentFeatureFlagsProvider featureFlags={preloadedData?.featureFlags ?? {}}>
    <AnalyticsSetup>
      <FragmentAppContent
        componentData={componentData}
        componentLibraryConfig={componentLibraryConfig}
        contentPath={contentPath}
      />
    </AnalyticsSetup>
  </FragmentFeatureFlagsProvider>
);

export default FragmentApp;
